import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { SectionHeading } from '../ui/SectionHeading';
import { Badge } from '../ui/Badge';
import { sampleSuppliers } from '../../data/suppliers';
import type { Supplier } from '../../data/schema';
import { motion } from 'framer-motion';
import { MapPinIcon, FilterIcon, BuildingIcon, ArrowRightIcon, XIcon } from 'lucide-react';
import { useLang } from '../../lib/i18n/LanguageContext';
import { dictionary } from '../../lib/i18n/dictionary';
function statusVariant(s: Supplier['verification_status']): 'pending' | 'success' | 'invite' | 'default' {
  if (s === 'Verified') return 'success';
  if (s === 'Invite Only') return 'invite';
  if (s === 'Pending Verification') return 'pending';
  return 'default';
}
interface Props {
  showHeader?: boolean;
  embedded?: boolean;
}
// Map URL filter param to logical filter key
const focusToFilter: Record<string, string> = {
  oilfield: 'API Related',
  electrical: 'Electrical Infrastructure',
  compliance: 'Certifications',
  methane: 'Methane Tech'
};
export function SupplierDirectory({
  showHeader = true,
  embedded = false
}: Props) {
  const {
    lang
  } = useLang();
  const t = dictionary.directory;
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  const [query, setQuery] = useState('');
  // Hydrate filter from URL param ?focus=oilfield etc.
  useEffect(() => {
    const focus = searchParams.get('focus');
    if (focus && focusToFilter[focus]) {
      setActiveFilters([focusToFilter[focus]]);
    }
  }, [searchParams]);
  const toggle = (f: string) => setActiveFilters(prev => prev.includes(f) ? prev.filter(x => x !== f) : [...prev, f]);
  const clearAll = () => {
    setActiveFilters([]);
    setQuery('');
    if (searchParams.get('focus')) {
      const next = new URLSearchParams(searchParams);
      next.delete('focus');
      setSearchParams(next, {
        replace: true
      });
    }
  };
  // Filter chips need to render in current language but evaluate against canonical EN keys
  const enFilters = dictionary.directory.filters.en;
  const localFilters = t.filters[lang];
  const filtered = useMemo(() => {
    let list = sampleSuppliers;
    if (query) {
      const q = query.toLowerCase();
      list = list.filter(s => s.company_name.toLowerCase().includes(q) || s.category.toLowerCase().includes(q) || s.state.toLowerCase().includes(q) || s.products_services.toLowerCase().includes(q));
    }
    if (activeFilters.length > 0) {
      list = list.filter(s => {
        return activeFilters.every(f => {
          if (f === 'Export Ready') return s.export_ready;
          if (f === 'Spanish Support') return s.spanish_support;
          if (f === 'API Related') return s.api_related;
          if (f === 'Methane Tech') return /methane/i.test(s.category);
          if (f === 'Electrical Infrastructure') return /electrical/i.test(s.category);
          if (f === 'Certifications') return s.certifications.length > 0;
          // Category / State / Product Type — soft filters: keep all for now
          return true;
        });
      });
    }
    return list;
  }, [query, activeFilters]);
  const Wrapper = embedded ? 'div' : 'section';
  return <Wrapper id="network" className={embedded ? '' : 'py-24 md:py-32 bg-white'} data-id="element-606">
      <div className={embedded ? '' : 'max-w-7xl mx-auto px-5 md:px-8'} data-id="element-607">
        {showHeader && <SectionHeading eyebrow={t.eyebrow[lang]} title={t.title[lang]} subtitle={t.subtitle[lang]} data-id="element-608" />}

        <div className={`${showHeader ? 'mt-10' : ''} border border-slate-200 rounded-md bg-slate-50/60 p-5`} data-id="element-609">
          <div className="flex flex-col md:flex-row gap-3 md:items-center" data-id="element-610">
            <div className="relative flex-1" data-id="element-611">
              <input value={query} onChange={e => setQuery(e.target.value)} placeholder={t.searchPlaceholder[lang]} className="w-full h-11 pl-10 pr-3 bg-white border border-slate-200 rounded-sm text-sm focus:outline-none focus:border-navy-deep" aria-label="Search suppliers" data-id="element-612" />
              <FilterIcon className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" data-id="element-613" />
            </div>
            <span className="font-mono text-[11px] text-slate-500 tracking-wider whitespace-nowrap" data-id="element-614">
              {filtered.length}{' '}
              {filtered.length === 1 ? t.sampleProfileSingular[lang] : t.sampleProfilesCount[lang]}
            </span>
          </div>

          <div className="mt-4 flex flex-wrap gap-2 items-center" data-id="element-615">
            {localFilters.map((f, i) => {
            const enKey = enFilters[i];
            const on = activeFilters.includes(enKey);
            return <button key={enKey} onClick={() => toggle(enKey)} className={`px-3 py-1.5 text-[12px] rounded-sm border transition-colors ${on ? 'bg-navy-deep text-white border-navy-deep' : 'bg-white text-slate-600 border-slate-200 hover:border-navy-deep/40'}`} data-id="element-616">
                  {f}
                </button>;
          })}
            {(activeFilters.length > 0 || query) && <button onClick={clearAll} className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 text-[12px] text-slate-500 hover:text-navy-deep" data-id="element-617">
                <XIcon className="w-3 h-3" data-id="element-618" />
                {t.clearFilters[lang]}
              </button>}
          </div>
        </div>

        {filtered.length === 0 ? <div className="mt-8 py-16 text-center border border-dashed border-slate-300 rounded-md text-slate-500 text-sm" data-id="element-619">
            {t.noResults[lang]}
          </div> : <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-620">
            {filtered.map((s, i) => <motion.article key={s.id} initial={{
          opacity: 0,
          y: 12
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-40px'
        }} transition={{
          duration: 0.45,
          delay: i % 6 * 0.04
        }} className="border border-slate-200 rounded-md bg-white p-5 hover:shadow-[0_8px_30px_rgb(10,22,40,0.06)] hover:border-navy-deep/30 transition-all group" data-id="element-621">
                <Link to={`/suppliers/${s.id}`} className="block focus:outline-none" data-id="element-622">
                  <div className="flex items-start justify-between gap-3 mb-3" data-id="element-623">
                    <div className="flex items-center gap-2.5" data-id="element-624">
                      <div className="w-9 h-9 flex items-center justify-center bg-navy-deep/5 border border-navy-deep/10 rounded-sm" data-id="element-625">
                        <BuildingIcon className="w-4 h-4 text-navy-deep" data-id="element-626" />
                      </div>
                      <Badge variant="gold" data-id="element-627">
                        {t.sampleProfileSingular[lang]}
                      </Badge>
                    </div>
                    <span className="font-mono text-[10px] text-slate-400" data-id="element-628">
                      {s.id}
                    </span>
                  </div>

                  <h3 className="font-serif text-lg text-navy-deep leading-tight mb-1 group-hover:text-petroleum transition-colors" data-id="element-629">
                    {s.company_name}
                  </h3>
                  <div className="flex items-center gap-1.5 text-[12.5px] text-slate-500 mb-4" data-id="element-630">
                    <MapPinIcon className="w-3.5 h-3.5" data-id="element-631" />
                    {s.city}, {s.state}
                  </div>

                  <dl className="space-y-2.5 text-[13px]" data-id="element-632">
                    <div data-id="element-633">
                      <dt className="font-mono text-[10.5px] text-slate-400 tracking-wider uppercase" data-id="element-634">
                        {t.fields.category[lang]}
                      </dt>
                      <dd className="text-navy-deep" data-id="element-635">{s.category}</dd>
                    </div>
                    <div data-id="element-636">
                      <dt className="font-mono text-[10.5px] text-slate-400 tracking-wider uppercase" data-id="element-637">
                        {t.fields.capabilities[lang]}
                      </dt>
                      <dd className="text-slate-600 leading-snug line-clamp-2" data-id="element-638">
                        {s.products_services}
                      </dd>
                    </div>
                    <div data-id="element-639">
                      <dt className="font-mono text-[10.5px] text-slate-400 tracking-wider uppercase" data-id="element-640">
                        {t.fields.certifications[lang]}
                      </dt>
                      <dd className="flex flex-wrap gap-1 mt-1" data-id="element-641">
                        {s.certifications.map(c => <Badge key={c} data-id="element-642">{c}</Badge>)}
                      </dd>
                    </div>
                  </dl>

                  <div className="mt-5 pt-4 border-t border-slate-200 flex flex-wrap gap-2 items-center justify-between" data-id="element-643">
                    <div className="flex flex-wrap gap-1.5" data-id="element-644">
                      {s.export_ready && <Badge data-id="element-645">{dictionary.common.exportReady[lang]}</Badge>}
                      {s.spanish_support && <Badge data-id="element-646">{dictionary.common.spanishSupport[lang]}</Badge>}
                    </div>
                    <Badge variant={statusVariant(s.verification_status)} data-id="element-647">
                      {t.statuses[s.verification_status][lang]}
                    </Badge>
                  </div>

                  <div className="mt-4 flex items-center gap-1.5 text-[12.5px] font-medium text-petroleum opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-648">
                    {dictionary.common.viewProfile[lang]}
                    <ArrowRightIcon className="w-3.5 h-3.5" data-id="element-649" />
                  </div>
                </Link>
              </motion.article>)}
          </div>}

        <p className="mt-8 text-xs text-slate-500 max-w-2xl" data-id="element-650">
          {t.illustrativeNote[lang]}
        </p>
      </div>
    </Wrapper>;
}